import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  X, Play, Pause, SkipForward, SkipBack, Film, Sparkles,
  Calendar, Tag as TagIcon, ChevronDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_SV = [
  'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
  'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
];

const FILTER_CSS = {
  none:     'none',
  vintage:  'sepia(0.4) contrast(1.1) saturate(0.85) brightness(0.97)',
  warm:     'contrast(1.12) saturate(1.35) hue-rotate(-8deg) brightness(1.05)',
  bw:       'grayscale(1) contrast(1.2)',
  dream:    'blur(0.6px) saturate(1.4) brightness(1.08) contrast(0.95)',
  polaroid: 'sepia(0.22) contrast(1.22) saturate(1.35) brightness(1.06)',
  film:     'sepia(0.32) contrast(1.18) saturate(0.88) hue-rotate(-12deg)',
  neon:     'saturate(1.8) contrast(1.25) hue-rotate(12deg)',
};

function Modal({ children, onClose, testId }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm p-3 flex items-center justify-center overflow-y-auto"
      onClick={onClose}
      data-testid={testId}
    >
      <div className="w-full max-w-2xl my-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/* ===================== YEARBOOK (Årsbok) ======================= */

function YearCard({ year, monthBreakdown }) {
  return (
    <div className="w-full h-full flex items-center justify-center text-center p-8 bg-gradient-to-br from-[#FFE8A3] via-[#FFE8F0] to-[#E8D5FF]">
      <div>
        <div className="text-6xl font-bold font-['Fredoka']">{year}</div>
        <div className="mt-2 text-sm opacity-80">
          {monthBreakdown.total} minnen · {monthBreakdown.distinctMonths} månader · {monthBreakdown.tags.length} taggar
        </div>
        <div className="mt-3 text-xs opacity-70">
          {monthBreakdown.tags.slice(0, 6).join(' · ')}
        </div>
      </div>
    </div>
  );
}

function MonthCard({ year, month, count }) {
  return (
    <div className="w-full h-full flex items-center justify-center text-center p-8 bg-gradient-to-br from-[#FFF7E0] to-[#FFE8F0]">
      <div>
        <div className="text-sm opacity-70 font-bold">{year}</div>
        <div className="text-4xl font-bold font-['Fredoka'] mt-1">{MONTH_SV[month]}</div>
        <div className="mt-2 text-sm opacity-80">{count} minnen</div>
      </div>
    </div>
  );
}

function EndCard({ year, total }) {
  return (
    <div className="w-full h-full flex items-center justify-center text-center p-8 bg-gradient-to-br from-[#C8B6FF] via-[#FFE8F0] to-[#B9FBC0]">
      <div>
        <div className="text-4xl mb-2">🎞️✨</div>
        <div className="text-3xl font-bold font-['Fredoka']">Slut på årsboken</div>
        <div className="mt-1 opacity-80">{year} — {total} mys-minnen</div>
      </div>
    </div>
  );
}

export function YearbookModal({ clips, onClose }) {
  const years = useMemo(() => {
    const s = new Set();
    clips.forEach((c) => s.add(new Date(c.memory_date).getFullYear()));
    return Array.from(s).sort((a, b) => b - a);
  }, [clips]);

  const [year, setYear] = useState(years[0] || new Date().getFullYear());

  // Build playlist: intro, then each clip chronologically with month intros when month changes, then end
  const playlist = useMemo(() => {
    const yearClips = clips
      .filter((c) => new Date(c.memory_date).getFullYear() === year)
      .sort((a, b) => new Date(a.memory_date) - new Date(b.memory_date));
    const tagSet = new Set();
    const monthSet = new Set();
    yearClips.forEach((c) => {
      (c.tags || []).forEach((t) => tagSet.add(t));
      monthSet.add(new Date(c.memory_date).getMonth());
    });
    const items = [];
    items.push({ kind: 'year', year, data: { total: yearClips.length, distinctMonths: monthSet.size, tags: Array.from(tagSet) } });
    let lastMonth = -1;
    yearClips.forEach((c) => {
      const m = new Date(c.memory_date).getMonth();
      if (m !== lastMonth) {
        const countInMonth = yearClips.filter((x) => new Date(x.memory_date).getMonth() === m).length;
        items.push({ kind: 'month', year, month: m, count: countInMonth });
        lastMonth = m;
      }
      items.push({ kind: 'clip', clip: c });
    });
    items.push({ kind: 'end', year, total: yearClips.length });
    return items;
  }, [clips, year]);

  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const videoRef = useRef(null);
  const cardTimerRef = useRef(null);

  useEffect(() => {
    setIdx(0);
  }, [year]);

  // Handle advancing on title cards (auto after 2.5s) and videos (on ended)
  useEffect(() => {
    if (!playing) return;
    if (idx >= playlist.length) return;
    const it = playlist[idx];
    if (it.kind !== 'clip') {
      const ms = it.kind === 'year' ? 3200 : it.kind === 'month' ? 2400 : 4500;
      cardTimerRef.current = setTimeout(() => setIdx((i) => Math.min(i + 1, playlist.length - 1)), ms);
      return () => clearTimeout(cardTimerRef.current);
    }
  }, [idx, playing, playlist]);

  const current = playlist[idx];
  const next = () => setIdx((i) => Math.min(i + 1, playlist.length - 1));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  if (years.length === 0) {
    return (
      <Modal onClose={onClose} testId="yearbook-modal">
        <div className="neo-card p-6 bg-white text-center">
          <p>Inga minnen än — spela in ditt första så kan vi göra en årsbok ✨</p>
          <button onClick={onClose} className="neo-btn mt-3 px-4 py-2 bg-[#FFC8DD] font-bold">Stäng</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} testId="yearbook-modal">
      <div className="flex items-center justify-between mb-2 text-white">
        <h3 className="font-bold flex items-center gap-2 text-lg">
          <Sparkles className="w-5 h-5" /> Årsbok {year}
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="neo-btn px-2 py-1 bg-white text-black text-sm font-bold"
            data-testid="yearbook-year-select"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white text-black" aria-label="Stäng" data-testid="yearbook-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="rounded-2xl border-[3px] border-black overflow-hidden bg-black" style={{ aspectRatio: '4 / 3' }}>
        {current?.kind === 'clip' ? (
          <video
            ref={videoRef}
            key={current.clip.id}
            src={`${API}/videos/${current.clip.id}/stream`}
            autoPlay={playing}
            playsInline
            className="w-full h-full object-contain"
            style={{ filter: FILTER_CSS[current.clip.filter] || 'none' }}
            onEnded={next}
            data-testid="yearbook-video"
          />
        ) : current?.kind === 'year' ? (
          <YearCard year={current.year} monthBreakdown={current.data} />
        ) : current?.kind === 'month' ? (
          <MonthCard year={current.year} month={current.month} count={current.count} />
        ) : (
          <EndCard year={current.year} total={current.total} />
        )}
      </div>

      {/* Caption line */}
      <div className="neo-card mt-2 p-3 bg-white text-sm">
        {current?.kind === 'clip' ? (
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-bold">{current.clip.caption || <span className="text-black/40 italic">Utan bildtext</span>}</p>
              <p className="text-xs text-black/60 mt-0.5">
                {new Date(current.clip.memory_date).toLocaleDateString('sv-SE')}
                {(current.clip.tags || []).length > 0 && <> · #{current.clip.tags.join(' #')}</>}
              </p>
            </div>
            <span className="text-xs opacity-60 whitespace-nowrap">{idx} / {playlist.length - 1}</span>
          </div>
        ) : (
          <p className="text-center text-xs opacity-70">Titelkort · {idx} / {playlist.length - 1}</p>
        )}
      </div>

      <div className="flex gap-2 mt-2">
        <button onClick={prev} disabled={idx === 0} className="neo-btn flex-1 py-2 bg-white disabled:opacity-50 flex items-center justify-center gap-1" data-testid="yearbook-prev">
          <SkipBack className="w-4 h-4" /> Föregående
        </button>
        <button onClick={() => setPlaying((p) => !p)} className="neo-btn flex-1 py-2 bg-[#FFE8A3] font-bold flex items-center justify-center gap-1" data-testid="yearbook-play-toggle">
          {playing ? <><Pause className="w-4 h-4" /> Paus</> : <><Play className="w-4 h-4" /> Spela</>}
        </button>
        <button onClick={next} disabled={idx >= playlist.length - 1} className="neo-btn flex-1 py-2 bg-white disabled:opacity-50 flex items-center justify-center gap-1" data-testid="yearbook-next">
          Nästa <SkipForward className="w-4 h-4" />
        </button>
      </div>
    </Modal>
  );
}

/* ===================== GROWTH ALBUM (Tillväxtalbum) ======================= */

export function GrowthAlbumModal({ clips, tag, onClose, onOpenClip }) {
  const tagClips = useMemo(
    () => clips.filter((c) => (c.tags || []).includes(tag))
      .sort((a, b) => new Date(a.memory_date) - new Date(b.memory_date)),
    [clips, tag]
  );

  const byYear = useMemo(() => {
    const map = new Map();
    tagClips.forEach((c) => {
      const y = new Date(c.memory_date).getFullYear();
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(c);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [tagClips]);

  return (
    <Modal onClose={onClose} testId="growth-album-modal">
      <div className="neo-card bg-gradient-to-br from-[#FFE8F0] via-[#FFF7E0] to-[#E8D5FF] p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-bold opacity-70 flex items-center gap-1"><TagIcon className="w-3 h-3" /> Tillväxtalbum</p>
            <h2 className="text-2xl font-bold font-['Fredoka']">{tag}</h2>
            <p className="text-xs opacity-70 mt-0.5">
              {tagClips.length} minnen · {byYear.length} år
            </p>
          </div>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" aria-label="Stäng" data-testid="growth-album-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {tagClips.length === 0 ? (
          <p className="text-center py-6 text-sm opacity-70">Inga minnen med taggen "{tag}" än.</p>
        ) : (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {byYear.map(([year, items]) => (
              <section key={year} data-testid={`growth-year-${year}`}>
                <h3 className="font-bold text-sm mb-1 flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> {year}
                  <span className="text-black/50 font-normal">· {items.length} minnen</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {items.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => onOpenClip?.(c)}
                      className="relative rounded-xl border-[3px] border-black overflow-hidden bg-black hover:translate-y-[-1px] transition"
                      style={{ aspectRatio: '4 / 3' }}
                      data-testid="growth-clip-thumb"
                    >
                      <video
                        src={`${API}/videos/${c.id}/stream`}
                        preload="metadata"
                        muted
                        playsInline
                        className="w-full h-full object-cover pointer-events-none"
                        style={{ filter: FILTER_CSS[c.filter] || 'none' }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 transition">
                        <Play className="w-6 h-6 text-white drop-shadow" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] font-mono px-1 py-0.5 text-center">
                        {new Date(c.memory_date).toLocaleDateString('sv-SE')}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
